/**
 * Client-Side JWT Decoder for monday.com Session Tokens
 *
 * Decodes the session token payload without verification (no secret needed).
 * Used to detect view-only users before making any API calls that would fail.
 */

interface SessionTokenDat {
  user_id?: number;
  account_id?: number;
  is_view_only?: boolean;
  isViewOnly?: boolean;
}

interface SessionTokenPayload {
  dat?: SessionTokenDat;
  exp?: number;
  iat?: number;
}

/**
 * Decode a JWT's payload segment without verification.
 * Returns null on any parse failure (safe fallback).
 */
export function decodeSessionTokenPayload(token: string): SessionTokenPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) return null;

    // Base64url → base64 → decode
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json) as SessionTokenPayload;
  } catch {
    return null;
  }
}

/**
 * Check if the session token indicates a view-only user.
 * Checks both `dat.is_view_only` and `dat.isViewOnly` to handle
 * monday.com's casing inconsistency.
 *
 * Returns false on any parse failure (safe fallback — auth flow continues
 * to existing error handling).
 */
export function isViewOnlyFromToken(token: string): boolean {
  const payload = decodeSessionTokenPayload(token);
  if (!payload?.dat) return false;
  return payload.dat.is_view_only === true || payload.dat.isViewOnly === true;
}
